import { useState, useCallback, useEffect } from 'react'

type CompareValuesFn<T> = (a: T, b: T) => boolean

interface FilterCallbacksOptions<T> {
    compareValues?: CompareValuesFn<T>
    onValueSaveCb?: () => void
    onValueResetCb?: () => void
    onResetStateCb?: () => void
}

export const useFilterCallbacks = <T>(
    initialValue: T,
    defaultValue: T,
    onChange: (value: T) => void,
    options: FilterCallbacksOptions<T> = {}
) => {
    const {
        compareValues = (a, b) => a === b,
        onValueSaveCb,
        onValueResetCb,
        onResetStateCb,
    } = options
    const [currentValue, setCurrentValue] = useState(initialValue)

    useEffect(() => {
        setCurrentValue(initialValue)
    }, [initialValue])

    const onValueSave = useCallback(() => {
        if (!compareValues(currentValue, initialValue)) {
            onChange(currentValue)
            onValueSaveCb?.()
        }
    }, [compareValues, currentValue, initialValue, onChange, onValueSaveCb])

    const onValueReset = useCallback(() => {
        if (!compareValues(defaultValue, initialValue)) {
            onChange(defaultValue)
        }
        setCurrentValue(defaultValue)
        onValueResetCb?.()
    }, [compareValues, defaultValue, initialValue, onChange, onValueResetCb])

    const resetState = useCallback(() => {
        setCurrentValue(initialValue)
        onResetStateCb?.()
    }, [initialValue, onResetStateCb])

    return {
        currentValue,
        setCurrentValue,
        onValueSave,
        onValueReset,
        resetState,
    }
}
